import React from "react";
import "../../../css/Customer/Pages/OurServicesPage/OurServicesPage.css";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import CardServices from "frontend/Components/DesignSystem/Cards/CardServices";
import OurGuide from "./OurGuide";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../assets/icons/Number 4.svg";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, SEO_SITE, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * Represents the OurServicesPage component which displays the main services offered by the company.
 * 
 * This page includes:
 * - Navigation bar with links to main site sections.
 * - Hero section with a title and subtitle describing the services.
 * - A set of service cards for Mortgage Planning, Retirement Planning, Equity Release, Corporate Planning, Insurance Advice, and Investment Planning.
 * - Informational sections explaining when financial advice is needed.
 * - Three "OurGuide" sections for different life stages, each with relevant products and advice.
 * - Adviser values section highlighting the company's approach.
 * - FAQ section answering common questions.
 * - Contact form and footer.
 * 
 * @component
 * @extends React.Component
 * 
 * @example
 * <OurServicesPage />
 */
class OurServicesPage extends React.Component {
  render() {
    const navigationItems = [
      { label: "Home", navigateTo: "/" },
      { label: "Our Services", navigateTo: "/OurServices" },
      { label: "About Us", navigateTo: "/AboutUs" },
      { label: "Contact Us", navigateTo: "/ContactUs" },
    ];
    const StartingYourCareerProducts = [
      {
        title: "Personal and Business Protection",
        content:
          "Providing you with tailored life, critical illness, income protection, and business cover.",
      },

      {
        title: "Investment Planning",
        content:
          "Advice on the right investment options for your needs and objectives.",
      },
    ];
    const EvolvingFinancialPrioritiesProducts = [
      {
        title: "Retirement Planning",
        content:
          "Advising on the right pension plan for you, considering the right product, investment fund and outcomes.",
      },
      {
        title: "Personal and Business Protection",
        content:
          "Providing you with tailored life, critical illness, income protection, and business cover.",
      },
      {
        title: "Investment Planning",
        content:
          "Advice on the right investment options for your needs and objectives.",
      },
      {
        title: "Mortgage Planning",
        content: "Advice on mortgages and tailoring plans to your needs.",
      },
    ];

    const NextFinancialStepsProducts = [
      {
        title: "Equity Release",
        content:
          "Advice on savings, investments, tax planning and retirement planning.",
      },
      {
        title: "Investment Planning",
        content:
          "Advice on the right investment options for your needs and objectives.",
      },
      {
        title: "Retirement Planning",
        content:
          "Advising on the right pension plan for you, considering the right product, investment fund and outcomes.",
      },
      {
        title: "Personal and Business Protection",
        content:
          "Providing you with tailored life, critical illness, income protection, and business cover.",
      },
    ];

    const values = [
      {
        icon: <NumberOneIcon />,
        text: "You are our most important person",
      },
      {
        icon: <NumberTwoIcon />,
        text: "Recommendations are planned out and delivered to you",
      },
      {
        icon: <NumberThreeIcon />,
        text: "Deliver the best outcomes for you",
      },
      {
        icon: <NumberFourIcon />,
        text: "We talk you through every step of the process",
      },
    ];

    const faqs = [
      {
        question: "When should I get an adviser?",
        answer:
          "Any stage of your life an adviser can help, but the earlier you speak with one, the more you can get from these conversations.",
      },
      {
        question: "What are the benefits of having an adviser?",
        answer:
          "Assistance with your finances and guidance with bespoke planning.",
      },
      {
        question: "What are the services you offer?",
        answer:
          "At Rosemount Financial Solutions (IFA) we offer Mortgage Planning, Retirement Planning, Equity Release, Corporate Planning, Insurance and Protection Advice and Investment Planning.",
      },
    ];

    const servicesStructuredData = {
      "@type": "ItemList",
      name: "Rosemount Financial Solutions Services",
      description: SEO_PAGES.ourServices.description,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@type": "Service",
            name: "Mortgage Planning",
            url: getPageUrl("/OurServices/MortgagePlanning"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Mortgage Advice",
            description:
              "Mortgage planning advice for first-time buyers, home movers, remortgage clients and those with complex mortgage needs.",
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@type": "Service",
            name: "Retirement Planning",
            url: getPageUrl("/OurServices/RetirementPlanning"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Retirement Planning",
            description:
              "Retirement and pension planning advice to help clients build a suitable long-term financial strategy.",
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@type": "Service",
            name: "Equity Release",
            url: getPageUrl("/OurServices/EquityRelease"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Equity Release Advice",
            description:
              "Equity release advice for homeowners looking to understand property wealth, lifetime mortgages, costs, risks and suitable alternatives.",
          },
        },
        {
          "@type": "ListItem",
          position: 4,
          item: {
            "@type": "Service",
            name: "Corporate Planning",
            url: getPageUrl("/OurServices/CorporatePlanning"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Corporate Planning",
            description:
              "Corporate planning support including business protection, relevant life cover, key person insurance and director pension planning.",
          },
        },
        {
          "@type": "ListItem",
          position: 5,
          item: {
            "@type": "Service",
            name: "Insurance and Protection Advice",
            url: getPageUrl("/OurServices/InsuranceProtection"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Protection Advice",
            description:
              "Insurance and protection advice including life cover, critical illness cover, income protection and family financial protection.",
          },
        },
        {
          "@type": "ListItem",
          position: 6,
          item: {
            "@type": "Service",
            name: "Investment Planning",
            url: getPageUrl("/OurServices/InvestmentPlanning"),
            provider: {
              "@id": `${SEO_SITE.siteUrl}/#organization`,
            },
            serviceType: "Investment Planning",
            description:
              "Investment planning advice based on client goals, risk tolerance, timeframe and investment preferences.",
          },
        },
      ],
    };

    return (
      <div className="Our-Services-Page">
        <SeoHelmet
          page={SEO_PAGES.ourServices}
          service={{
            name: "Financial Advice Services",
            description:
              "Rosemount Financial Solutions provides mortgage planning, retirement planning, investment planning, equity release, corporate planning and insurance/protection advice.",
            serviceType: [
              "Mortgage Planning",
              "Retirement Planning",
              "Investment Planning",
              "Equity Release Advice",
              "Corporate Planning",
              "Insurance Advice",
              "Protection Advice",
              "Financial Planning",
            ],
          }}
          breadcrumbs={[
            {
              name: "Home",
              url: getPageUrl("/"),
            },
            {
              name: "Our Services",
              url: getPageUrl("/OurServices"),
            },
          ]}
          faqs={faqs}
          extraSchemas={[servicesStructuredData]}
        />
        <NavBar navigationItems={navigationItems} />
        <HeroAnimation
          imgSrc={ImageDirectory["customer-services-min"]}
          altText="A contract set in a binder with two sets of hands one on the top with a pen in hand."
          // imgSrcSmall={CustomerServicesSmallImage}
          children={
            <>
              <h1>Our Services</h1>
              <div className="SubTitle">
                We offer Mortgage Planning, Investment Planning, Equity Release,
                Retirement Planning, Corporate Planning and Insurance/Protection
                Advice
              </div>
            </>
          }
        />
        <div className="Our-Services-Container">
          <h2>Our Services</h2>
          <div className="Our-Services-Content">
            <CardServices
              imgSrc={ImageDirectory["customer-mortgage-min"]}
              imgSrcSmall={ImageDirectory["customer-mortgage-small-min"]}
              altText="A couple holding a set of house keys with a home keyring."
              title="Mortgage Planning"
              content="Whether you are a first time buyer, home mover, or looking to remortgage, we can help find you the most competitive rates and provide the right solution tailored for you."
              link="/OurServices/MortgagePlanning"
            />
            <CardServices
              imgSrc={ImageDirectory["customer-retirement-min"]}
              imgSrcSmall={ImageDirectory["customer-retirement-small-min"]}
              altText="A couple on a swing set on an autumnal day with brown leaves in the background."
              title="Retirement Planning"
              content="Our advisers will put together a bespoke retirement plan for you, ensuring you have the most suitable solution for your needs."
              link="/OurServices/RetirementPlanning"
            />
            <CardServices
              imgSrc={ImageDirectory["customer-equity-min"]}
              imgSrcSmall={ImageDirectory["customer-equity-small-min"]}
              altText="A couple sat together cuddling and reading a hardback book."
              title="Equity Release"
              content="If you are looking to release equity in your home, perhaps to gift money to loved ones or fund home improvements, our advisers will recommend a suitable approach for your needs."
              link="/OurServices/EquityRelease"
            />
            <CardServices
              imgSrc={ImageDirectory["customer-corporate-min"]}
              imgSrcSmall={ImageDirectory["customer-corporate-small-min"]}
              altText="A meeting room with ten empty seats around the table."
              title="Corporate Planning"
              content="Looking after your people is important to safeguard the long term financial health of your business. We can help you plan based on your specific business needs."
              link="/OurServices/CorporatePlanning"
            />
            <CardServices
              imgSrc={ImageDirectory["customer-insurance-min"]}
              imgSrcSmall={ImageDirectory["customer-insurance-small-min"]}
              altText="Home insurance policy papers with a pair of glasses and a pen lay on top."
              title="Insurance Advice"
              content="Financial protection planning can bring peace of mind in the event of the unexpected happens. With protection, you and your dependents can continue without missing a step."
              link="/OurServices/InsuranceProtection"
            />
            <CardServices
              imgSrc={ImageDirectory["customer-investment-min"]}
              imgSrcSmall={ImageDirectory["customer-investment-small-min"]}
              altText="Two advisers looking at a graph on paper of a bar chart and a laptop with a bar graph on."
              title="Investment Planning"
              content="People save and invest money for many different reasons. Whether you are looking to invest for income or growth, we can help you achieve your financial goals."
              link="/OurServices/InvestmentPlanning"
            />
          </div>
        </div>
        <div className="Services-Info-box1">
          <div className="Services-Info-box1-Content">
            <h2>When do I need financial advice?</h2>
            <div className="SubTitle">
              With different types of financial advice our advisers can ensure
              you are getting what you need with tailored plans to fit your
              needs. They will be with you every step of the journey.
            </div>
          </div>
        </div>
        <div>
          <OurGuide
            imgSrc={ImageDirectory["customer-starting"]}
            imgSrcSmall={ImageDirectory["customer-starting-small"]}
            altText="An adviser sat at a table with a mug and a laptop scrolling in one hand and writing on a tablet in the other."
            title="Starting Your Career"
            content="Even at the start of your career, there is significant value in speaking with an adviser. Request a call back or complete our contact form to find out more."
            guideProductTitle="At This Stage of Life, a Financial Adviser Can Assist You With:"
            products={StartingYourCareerProducts}
          />
          <div className="divider"></div>
        </div>
        <div>
          <OurGuide
            imgSrc={ImageDirectory["customer-evolving"]}
            imgSrcSmall={ImageDirectory["customer-evolving-small"]}
            altText="A hand holding a set of keys."
            title="Evolving Financial Priorities"
            content="Taking the next financial steps, your needs can change. Advisers can help you look at upgrading or changing previous significant purchases. This stage is a time to start looking into which retirement plan will be best for you going forward. Discussing with our advisers, they will ensure you are getting tailored plans for the future you deserve."
            guideProductTitle="At This Stage of Life, a Financial Adviser Can Assist You With:"
            products={EvolvingFinancialPrioritiesProducts}
          />
          <div className="divider"></div>
        </div>
        <OurGuide
          imgSrc={ImageDirectory["customer-new financial"]}
          imgSrcSmall={ImageDirectory["customer-new financial-small"]}
          altText="A desk with paperwork, notes and a calculator on with two hands both holding pens."
          title="Next Financial Steps"
          content="Several changes may be happening including looking to retire or move to part-time work, changing significant purchases like moving house or paying off your mortgage. All of these will be smoother with a financial adviser guiding you through the journey. Bespoke plans for ensuring your wealth is inherited as per your wishes."
          guideProductTitle="At This Stage of Life, a Financial Adviser Can Assist You With:"
          products={NextFinancialStepsProducts}
        />
        <div className="Services-Info-box2">
          <div className="Services-Info-box1-Content SubTitle">
            Your home may be repossessed if you do not keep up repayments on
            your mortgage. The value of pensions and investments and the income
            they produce can fall as well as rise. You may get back less than
            you invested
          </div>
        </div>
        <AdviserValues values={values} title="Our Advisers Values" />
        <FAQContainer faqs={faqs} />
        <ContactUs isAdviser={false} />
        <Footer />
      </div>
    );
  }
}

export default OurServicesPage;
